import { readCustomFieldAnswers } from "@/data/mappers/custom-fields.mapper";
import {
  reservationLookupStates,
  type ConfirmedReservation,
  type PartnerReservation,
  type RecordedArrival,
  type ReservationLookup,
  type ReservationLookupState,
} from "@/domain/reservation/types";

export interface PartnerReservationPayload {
  id: number;
  offerId: number;
  state: string | null;
  createdAt: string | null;
  startTime: string | null;
  amount: number | null;
  code: string | null;
  confirmDeadline: string | null;
  validatedAt: string | null;
  activityName: string | null;
  customerName: string | null;
  customAnswers?: unknown;
  cancellationReason?: string | null;
}

export interface ReservationLookupPayload {
  id: number;
  state: string | null;
  activityName: string | null;
  customerName: string | null;
  startTime: string | null;
  amount: number | null;
  validatedAt?: string | null;
  customAnswers?: unknown;
}

export interface RecordedArrivalPayload {
  validatedAt?: string | null;
}

export interface ConfirmedReservationPayload {
  id: number;
  code: string | null;
  confirmedAt: string | null;
}

// A timestamp the payload never carried must read as absent: an Invalid Date is
// truthy, and would state an arrival that was never recorded.
export function toDate(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function payloadToPartnerReservation(
  payload: PartnerReservationPayload,
): PartnerReservation {
  return {
    id: payload.id,
    offerId: payload.offerId,
    state: payload.state ?? "unknown",
    activityName: payload.activityName ?? "Neznámá šablona slev",
    customerName: payload.customerName,
    createdAt: toDate(payload.createdAt),
    startTime: toDate(payload.startTime),
    amountMinor: payload.amount ?? 0,
    code: payload.code,
    confirmDeadline: toDate(payload.confirmDeadline),
    validatedAt: toDate(payload.validatedAt),
    customAnswers: readCustomFieldAnswers(payload.customAnswers ?? null, "custom_answers") ?? [],
    cancellationReason: payload.cancellationReason ?? null,
  };
}

function toLookupState(state: string | null): ReservationLookupState {
  return reservationLookupStates.find((known) => known === state) ?? "unknown";
}

export function payloadToReservationLookup(payload: ReservationLookupPayload): ReservationLookup {
  return {
    id: payload.id,
    state: toLookupState(payload.state),
    activityName: payload.activityName ?? "Neznámá šablona slev",
    customerName: payload.customerName,
    startTime: toDate(payload.startTime),
    amountMinor: payload.amount ?? 0,
    validatedAt: toDate(payload.validatedAt),
    customAnswers: readCustomFieldAnswers(payload.customAnswers ?? null, "custom_answers") ?? [],
  };
}

export function payloadToRecordedArrival(payload: RecordedArrivalPayload): RecordedArrival {
  return { validatedAt: toDate(payload.validatedAt) };
}

export function payloadToConfirmedReservation(
  payload: ConfirmedReservationPayload,
): ConfirmedReservation {
  return {
    id: payload.id,
    code: payload.code,
    confirmedAt: toDate(payload.confirmedAt),
  };
}
